import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";

/*
  Tuning section for quick visual edits.
  Change values here to adjust the whole carousel template.
*/
const UI = {
  sideNavWidthPx: 80,
  modalWidthPx: 392,
  modalHeightPx: 590,
  modalViewportOffsetPx: 24, // keeps a small safe offset from viewport edges
  modalRadiusPx: 24,
  colors: {
    bgTop: "#020611",
    bgMid: "#061022",
    bgBottom: "#0B1E3A",
    edgeGlow: "rgba(62,130,228,0.16)",
    summaryBg: "#12326B",
    consentBg: "#102753",
  },

  image: {
    default: {
      widthPx: 132,
      heightPx: 132,
      anchorTopPx: 6,
      offsetYPx: 0,
      offsetXPx: 0,
      reserveSpaceHeightPx: 120,
      radiusPx: 24,
      fitMode: "contain",
    },
  },

  typography: {
    titleSizePx: 28,
    titleLineHeight: 1.12,
    bodySizePx: 16,
    bodyLineHeight: 1.38,
    bodyMaxWidthPercent: 85,
    summarySizePx: 16,
    summaryLineHeight: 1.35,
  },

  spacing: {
    topCounterMarginBottomPx: 2,
    imageMarginBottomPx: 0,
    titleMarginBottomPx: 10,
    bodyMarginBottomPx: 16,
    summaryMarginBottomPx: 8,
    dotsMarginBottomPx: 12,
  },
};

const Dot = ({ active }) => (
  <span
    className={`h-2.5 w-2.5 rounded-full transition ${
      active ? "bg-blue-400" : "bg-white/30"
    }`}
  />
);

const GameDot = ({ active }) => (
  <span
    className="h-2.5 w-2.5 rounded-full transition"
    style={{
      background: active ? "#4FFFF0" : "rgba(255,255,255,0.24)",
      boxShadow: active ? "0 0 8px rgba(79,255,240,0.34)" : "none",
    }}
  />
);

const IconBadge = ({ icon, imageLayout, width, height }) => (
  <div
    className="relative mx-auto border border-blue-200/25 bg-[radial-gradient(circle_at_30%_20%,rgba(103,179,255,0.35),rgba(7,22,60,0.86)_58%,rgba(6,12,31,0.94)_100%)] shadow-[0_18px_44px_rgba(0,70,255,0.38)] flex items-center justify-center"
    style={{
      width: width || `${imageLayout.widthPx}px`,
      height: height || `${imageLayout.heightPx}px`,
      borderRadius: `${imageLayout.radiusPx}px`,
    }}
  >
    <div className="absolute inset-0 bg-[linear-gradient(145deg,rgba(255,255,255,0.18),rgba(255,255,255,0)_42%)]" />
    <span className="relative text-4xl leading-none">{icon || "✨"}</span>
  </div>
);

export default function FirstJoinRulesCarouselModal({
  isOpen,
  cards,
  onClose,
  onConfirm,
  isSubmitting = false,
  i18nKeyPrefix = "events.details.first_join_rules",
  emphasizePrimaryAction = false,
  primaryActionNoWrap = false,
  initialIndex = 0,
  onCardIndexChange,
  modalAriaLabel,
  headerTitle,
  showCounter = false,
  showDots = true,
  closeAriaLabel,
  backLabel,
  nextLabel,
  finalLabel,
  submittingLabel,
  requireConsentOnLastCard = true,
  consentLabel,
  theme = "default",
  allowEarlyClose = true,
}) {
  const { t } = useTranslation("events");
  const [index, setIndex] = useState(0);
  const [consentChecked, setConsentChecked] = useState(false);
  const [imageFailed, setImageFailed] = useState({});
  const [copyVisibleIndex, setCopyVisibleIndex] = useState(null);
  const [confirmInFlight, setConfirmInFlight] = useState(false);
  const [consentAttention, setConsentAttention] = useState(false);
  const [scrollHintVisible, setScrollHintVisible] = useState(false);
  const confirmInFlightRef = React.useRef(false);
  const preloadImagesRef = React.useRef([]);
  const touchStartXRef = React.useRef(null);
  const touchStartYRef = React.useRef(null);
  const cardScrollRef = React.useRef(null);
  const consentRef = React.useRef(null);
  const consentAttentionTimeoutRef = React.useRef(null);
  const safeCards = useMemo(() => (Array.isArray(cards) ? cards : []), [cards]);
  const total = safeCards.length;
  const isGameTheme = theme === "game";
  const current = safeCards[index] || null;
  const isLast = index === total - 1;
  const hasVisualNode = Boolean(current?.visualNode);
  const hideVisual = Boolean(current?.hideVisual);
  const effectiveHeaderTitle = current?.headerTitle ?? headerTitle;
  const effectiveIsSubmitting = isSubmitting || confirmInFlight;
  const canDismiss = (allowEarlyClose || isLast) && !effectiveIsSubmitting;
  const showCardCopy = !isGameTheme || !hasVisualNode || copyVisibleIndex === index;
  const visualNode =
    isGameTheme && hasVisualNode && React.isValidElement(current?.visualNode)
      ? React.cloneElement(current.visualNode, { showBottomFade: showCardCopy })
      : current?.visualNode;

  useEffect(() => {
    if (!isOpen) return;
    const maxIndex = Math.max(0, total - 1);
    const safeInitial = Number.isFinite(Number(initialIndex))
      ? Math.max(0, Math.min(maxIndex, Math.floor(Number(initialIndex))))
      : 0;
    setIndex(safeInitial);
    setConsentChecked(false);
    setImageFailed({});
    setConfirmInFlight(false);
    setConsentAttention(false);
    confirmInFlightRef.current = false;
  }, [initialIndex, isOpen, total]);

  useEffect(() => {
    return () => {
      if (consentAttentionTimeoutRef.current != null) {
        window.clearTimeout(consentAttentionTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen || typeof onCardIndexChange !== "function") return;
    onCardIndexChange(index);
  }, [index, isOpen, onCardIndexChange]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape" && canDismiss && typeof onClose === "function") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [canDismiss, isOpen, onClose]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    if (!(isGameTheme && hasVisualNode)) {
      setCopyVisibleIndex(index);
      return undefined;
    }

    setCopyVisibleIndex(null);
    const targetIndex = index;
    const timeoutId = window.setTimeout(() => {
      setCopyVisibleIndex(targetIndex);
    }, Number(current?.visualDelayMs) || 3200);

    return () => window.clearTimeout(timeoutId);
  }, [current?.visualDelayMs, hasVisualNode, index, isGameTheme, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || isGameTheme) {
      setScrollHintVisible(false);
      return undefined;
    }

    const node = cardScrollRef.current;
    if (!node) return undefined;
    const updateScrollHint = () => {
      setScrollHintVisible(
        node.scrollTop + node.clientHeight < node.scrollHeight - 2
      );
    };

    updateScrollHint();
    window.addEventListener("resize", updateScrollHint, { passive: true });
    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(updateScrollHint)
        : null;
    resizeObserver?.observe(node);

    return () => {
      window.removeEventListener("resize", updateScrollHint);
      resizeObserver?.disconnect();
    };
  }, [index, isGameTheme, isOpen]);

  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return;

    const srcs = new Set();
    safeCards.forEach((card) => {
      if (card?.imageSrc) srcs.add(card.imageSrc);
      if (Array.isArray(card?.preloadImageSrcs)) {
        card.preloadImageSrcs.forEach((src) => {
          if (src) srcs.add(src);
        });
      }
    });

    preloadImagesRef.current = [...srcs].map((src) => {
      const image = new window.Image();
      image.decoding = "async";
      image.src = src;
      return image;
    });
  }, [isOpen, safeCards]);

  const canConfirm =
    !effectiveIsSubmitting &&
    (!isLast || !requireConsentOnLastCard || consentChecked);

  const handleClose = (event) => {
    if (!canDismiss) return;
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const stopMapGesturePropagation = (event) => {
    event.stopPropagation();
  };
  const imageLayout = useMemo(() => {
    const base = UI.image.default;
    const override = current?.visualLayout || {};
    return {
      widthPx: override.widthPx ?? base.widthPx,
      heightPx: override.heightPx ?? base.heightPx,
      anchorTopPx: override.anchorTopPx ?? base.anchorTopPx,
      offsetYPx: override.offsetYPx ?? base.offsetYPx,
      offsetXPx: override.offsetXPx ?? base.offsetXPx,
      reserveSpaceHeightPx: override.reserveSpaceHeightPx ?? base.reserveSpaceHeightPx,
      radiusPx: override.radiusPx ?? base.radiusPx,
      fitMode: override.fitMode ?? base.fitMode,
      baseAnchorTopPx: base.anchorTopPx,
    };
  }, [current?.visualLayout]);

  const prepareCardCopyForIndex = (nextIndex) => {
    const nextCard = safeCards[nextIndex] || null;
    if (isGameTheme && nextCard?.visualNode) {
      setCopyVisibleIndex(null);
      return;
    }
    setCopyVisibleIndex(nextIndex);
  };

  const goBack = () => {
    if (index === 0 || effectiveIsSubmitting) return;
    const nextIndex = Math.max(0, index - 1);
    prepareCardCopyForIndex(nextIndex);
    setIndex(nextIndex);
  };

  const goNext = async () => {
    if (effectiveIsSubmitting || confirmInFlightRef.current) return;
    if (!isLast) {
      const nextIndex = Math.min(total - 1, index + 1);
      prepareCardCopyForIndex(nextIndex);
      setIndex(nextIndex);
      return;
    }
    if (requireConsentOnLastCard && !consentChecked) {
      consentRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      consentRef.current?.querySelector("input")?.focus?.({ preventScroll: true });
      setConsentAttention(true);
      if (consentAttentionTimeoutRef.current != null) {
        window.clearTimeout(consentAttentionTimeoutRef.current);
      }
      consentAttentionTimeoutRef.current = window.setTimeout(() => {
        setConsentAttention(false);
      }, 1400);
      return;
    }
    if (canConfirm && typeof onConfirm === "function") {
      confirmInFlightRef.current = true;
      setConfirmInFlight(true);
      try {
        await onConfirm();
      } finally {
        confirmInFlightRef.current = false;
        setConfirmInFlight(false);
      }
    }
  };

  const handleTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  };

  const handleTouchEnd = (event) => {
    const touch = event.changedTouches?.[0];
    const startX = touchStartXRef.current;
    const startY = touchStartYRef.current;
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    if (!touch || startX == null || startY == null) return;

    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;
    if (Math.abs(dx) < 42 || Math.abs(dx) < Math.abs(dy) * 1.15) return;

    if (dx < 0) {
      goNext();
      return;
    }
    goBack();
  };

  if (!isOpen || !current || total === 0) return null;

  const hasImage = current.imageSrc && !imageFailed[index];
  const BodyContainer = current.bodyNode ? "div" : "p";
  const usesDefaultVisualSize =
    imageLayout.widthPx === UI.image.default.widthPx &&
    imageLayout.heightPx === UI.image.default.heightPx;
  const responsiveVisualWidth = isGameTheme || !usesDefaultVisualSize
    ? `${imageLayout.widthPx}px`
    : `min(${imageLayout.widthPx}px, clamp(88px, 15.5vh, 132px))`;
  const responsiveVisualHeight = isGameTheme || !usesDefaultVisualSize
    ? `${imageLayout.heightPx}px`
    : `min(${imageLayout.heightPx}px, clamp(88px, 15.5vh, 132px))`;

  const inlineVisual = !hideVisual && !(isGameTheme && hasVisualNode) ? (
    <div
      className="relative z-[5] mx-auto shrink-0 pointer-events-none"
      style={{
        width: responsiveVisualWidth,
        height: responsiveVisualHeight,
        marginTop: `${Math.max(0, imageLayout.anchorTopPx)}px`,
        marginBottom: `min(${Math.max(12, current?.visualTitleGapPx ?? 16)}px, 2vh)`,
        transform: `translate(${imageLayout.offsetXPx}px, ${imageLayout.offsetYPx}px)`,
      }}
    >
      <div
        className={`relative overflow-hidden ${
          isGameTheme
            ? "border bg-[linear-gradient(180deg,rgba(14,36,52,0.95),rgba(8,17,24,0.96))] shadow-[0_10px_32px_rgba(0,0,0,0.38)]"
            : "border border-blue-200/25 bg-[radial-gradient(circle_at_30%_20%,rgba(103,179,255,0.35),rgba(7,22,60,0.86)_58%,rgba(6,12,31,0.94)_100%)] shadow-[0_18px_44px_rgba(0,70,255,0.38)]"
        }`}
        style={{
          width: responsiveVisualWidth,
          height: responsiveVisualHeight,
          borderRadius: `${imageLayout.radiusPx}px`,
          borderColor: isGameTheme ? "rgba(79,255,240,0.34)" : undefined,
          boxShadow: isGameTheme
            ? "0 0 20px rgba(79,255,240,0.14), 0 10px 32px rgba(0,0,0,0.38)"
            : undefined,
        }}
      >
        {hasVisualNode ? (
          <div
            className="h-full w-full"
            style={{
              width: responsiveVisualWidth,
              height: responsiveVisualHeight,
            }}
          >
            {visualNode}
          </div>
        ) : hasImage ? (
          <img
            src={current.imageSrc}
            alt=""
            draggable={false}
            style={{
              width: responsiveVisualWidth,
              height: responsiveVisualHeight,
              objectFit: imageLayout.fitMode,
            }}
            className="mx-auto"
            onError={() => {
              setImageFailed((prev) => ({ ...prev, [index]: true }));
            }}
          />
        ) : (
          <IconBadge
            icon={current.icon}
            imageLayout={imageLayout}
            width={responsiveVisualWidth}
            height={responsiveVisualHeight}
          />
        )}
      </div>
    </div>
  ) : null;

  const content = (
    <div
      data-vg-game-modal-gesture-shield="true"
      className="relative flex min-h-0 flex-1 flex-col overflow-hidden text-white"
      style={{
        touchAction: "pan-y",
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={stopMapGesturePropagation}
      onMouseMove={stopMapGesturePropagation}
      onMouseUp={stopMapGesturePropagation}
      onPointerDown={stopMapGesturePropagation}
      onPointerMove={stopMapGesturePropagation}
      onPointerUp={stopMapGesturePropagation}
      onPointerCancel={stopMapGesturePropagation}
      onTouchStart={handleTouchStart}
      onTouchMove={stopMapGesturePropagation}
      onTouchEnd={handleTouchEnd}
    >
      {isGameTheme ? (
        <>
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(79,255,240,0.12),transparent_42%),radial-gradient(circle_at_bottom,rgba(255,106,0,0.08),transparent_32%)]" />
          <div className="pointer-events-none absolute inset-0 rounded-[24px] border border-cyan-300/18" />
        </>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col">
        <div
          className={`relative z-10 text-base font-semibold text-white ${
            showCounter ? (isGameTheme ? "min-h-[36px]" : "h-0 text-right") : "hidden"
          }`}
          style={{ marginBottom: `${UI.spacing.topCounterMarginBottomPx}px` }}
        >
          {showCounter ? (
            <div
              className="absolute left-0 top-0 text-left text-[15px] font-black italic tracking-[0.06em]"
              style={{
                color: isGameTheme ? "#4FFFF0" : undefined,
                textShadow: isGameTheme ? "0 0 12px rgba(79,255,240,0.22)" : undefined,
              }}
            >
              {`${index + 1}/${total}`}
            </div>
          ) : null}
        </div>

        {hideVisual ? null : isGameTheme && hasVisualNode ? (
          <div className="absolute inset-x-0 top-0 bottom-0 overflow-hidden rounded-[24px]">
            <div
              className="absolute inset-x-0 top-0"
              style={{
                height: "100%",
              }}
            >
              {visualNode}
            </div>
            <div
              className="pointer-events-none absolute inset-0 bg-[rgba(5,12,16,0.68)] transition-opacity duration-500"
              style={{
                opacity: showCardCopy ? 1 : 0,
              }}
            />
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-[320px] bg-[linear-gradient(to_top,rgba(5,12,16,0.99),rgba(5,12,16,0.94)_28%,rgba(5,12,16,0.66)_62%,rgba(5,12,16,0)_100%)] transition-opacity duration-500"
              style={{
                opacity: showCardCopy ? 1 : 0,
              }}
            />
          </div>
        ) : null}

        <div className="relative z-10 min-h-0 flex-1 overflow-hidden">
          {/* A fresh node prevents stale iOS WebView scroll/compositing layers between cards. */}
          <div
            ref={cardScrollRef}
            key={`join-rules-card-${index}`}
            data-testid="join-rules-card-scroll"
            data-card-index={index}
            className={`h-full min-h-0 ${
              isGameTheme && hasVisualNode ? "flex flex-col overflow-hidden" : "overflow-y-auto overscroll-contain"
            } px-1 pb-1`}
            onScroll={(event) => {
              const node = event.currentTarget;
              setScrollHintVisible(
                node.scrollTop + node.clientHeight < node.scrollHeight - 2
              );
            }}
          >
            <div className={isGameTheme && hasVisualNode ? "mt-auto px-4 pb-2" : ""}>
              {inlineVisual}
              <div
                style={{
                  marginBottom: `${UI.spacing.imageMarginBottomPx}px`,
                }}
              />

              <h3
                className={isGameTheme ? "text-center font-black italic uppercase tracking-[0.02em] transition-opacity duration-500" : "text-center font-bold tracking-tight text-white"}
                style={{
                  fontSize: isGameTheme
                    ? "17px"
                    : `clamp(23px, 3.3vh, ${UI.typography.titleSizePx}px)`,
                  lineHeight: isGameTheme ? 1.15 : UI.typography.titleLineHeight,
                  marginBottom: `${UI.spacing.titleMarginBottomPx}px`,
                  color: isGameTheme ? "#F4FFFE" : undefined,
                  opacity: isGameTheme && hasVisualNode ? (showCardCopy ? 1 : 0) : 1,
                  transform: isGameTheme && hasVisualNode
                    ? `translateY(${showCardCopy ? "0px" : "12px"})`
                    : undefined,
                }}
              >
                {current.title}
              </h3>

              <BodyContainer
                className="mx-auto text-center text-white/88 transition-opacity duration-500"
                style={{
                  maxWidth: `${UI.typography.bodyMaxWidthPercent}%`,
                  fontSize: `${UI.typography.bodySizePx}px`,
                  lineHeight: UI.typography.bodyLineHeight,
                  marginBottom: `${UI.spacing.bodyMarginBottomPx}px`,
                  opacity: isGameTheme && hasVisualNode ? (showCardCopy ? 1 : 0) : 1,
                  transform: isGameTheme && hasVisualNode
                    ? `translateY(${showCardCopy ? "0px" : "12px"})`
                    : undefined,
                }}
              >
                {current.bodyNode || current.body}
              </BodyContainer>

              {!isGameTheme && (current.summaryNode || current.summary) ? (
                <div
                  className="flex items-center gap-2 rounded-2xl border border-blue-300/30 p-3 text-left text-white/95"
                  style={{
                    background: UI.colors.summaryBg,
                    fontSize: `${UI.typography.summarySizePx}px`,
                    lineHeight: UI.typography.summaryLineHeight,
                    marginBottom: `${UI.spacing.summaryMarginBottomPx}px`,
                  }}
                >
                  <span className="text-base leading-none">{current.summaryIcon || "💬"}</span>
                  <span>{current.summaryNode || current.summary}</span>
                </div>
              ) : null}

              {isLast && requireConsentOnLastCard ? (
                <label
                  ref={consentRef}
                  className={`mb-1 flex cursor-pointer items-start gap-2 rounded-xl border px-3 py-2.5 text-sm text-white/95 transition-[border-color,box-shadow,transform] duration-300 ${
                    consentAttention
                      ? "scale-[1.015] border-amber-300 shadow-[0_0_0_3px_rgba(252,211,77,0.55),0_0_24px_rgba(252,211,77,0.3)]"
                      : "border-blue-300/30"
                  }`}
                  style={{ background: UI.colors.consentBg }}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-blue-500"
                    checked={consentChecked}
                    onChange={(e) => {
                      setConsentChecked(e.target.checked);
                      setConsentAttention(false);
                    }}
                  />
                  <span>{consentLabel || t(`${i18nKeyPrefix}.consent_label`)}</span>
                </label>
              ) : null}
            </div>
          </div>
          {!isGameTheme && scrollHintVisible ? (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-1 bottom-0 z-20 h-9 bg-gradient-to-t from-[#07152b] to-transparent"
            />
          ) : null}
        </div>
      </div>
    </div>
  );

  const footerOpacity = isGameTheme && hasVisualNode ? (showCardCopy ? 1 : 0) : 1;
  const footerPointerEvents = isGameTheme && hasVisualNode && !showCardCopy ? "none" : "auto";
  const modalTitle = effectiveHeaderTitle || modalAriaLabel || t(`${i18nKeyPrefix}.modal_aria`);
  const primaryLabel = effectiveIsSubmitting
    ? submittingLabel || t(`${i18nKeyPrefix}.submitting`)
    : isLast
    ? finalLabel || t(`${i18nKeyPrefix}.join_button`)
    : nextLabel || t(`${i18nKeyPrefix}.next_button`);
  const awaitingConsent = isLast && requireConsentOnLastCard && !consentChecked;

  const carouselFooter = (
    <div
      className="transition-opacity duration-500"
      style={{
        opacity: footerOpacity,
        pointerEvents: footerPointerEvents,
      }}
    >
      {showDots ? (
        <div
          className="flex items-center justify-center gap-2"
          style={{ marginBottom: `${UI.spacing.dotsMarginBottomPx}px` }}
        >
          {safeCards.map((_, cardIndex) => (
            isGameTheme ? (
              <GameDot key={`join-rules-dot-${cardIndex}`} active={cardIndex === index} />
            ) : (
              <Dot key={`join-rules-dot-${cardIndex}`} active={cardIndex === index} />
            )
          ))}
        </div>
      ) : null}

      <div className="flex gap-3">
        <AppModalButton
          type="button"
          variant="secondary"
          className={`${emphasizePrimaryAction && isLast ? "basis-[38%]" : "flex-1"}`}
          onClick={goBack}
          disabled={index === 0 || effectiveIsSubmitting}
        >
          {backLabel || t(`${i18nKeyPrefix}.back_button`)}
        </AppModalButton>

        <AppModalButton
          type="button"
          className={`${emphasizePrimaryAction && isLast ? "basis-[62%]" : "flex-1"} ${
            primaryActionNoWrap ? "whitespace-nowrap" : ""
          }`}
          onClick={goNext}
          disabled={effectiveIsSubmitting}
          aria-disabled={!effectiveIsSubmitting && !canConfirm}
          data-awaiting-consent={awaitingConsent ? "true" : undefined}
          style={awaitingConsent ? {
            backgroundColor: "rgba(255,255,255,0.16)",
            color: "rgba(255,255,255,0.42)",
            boxShadow: "none",
            cursor: "not-allowed",
          } : undefined}
        >
          {primaryLabel}
        </AppModalButton>
      </div>
    </div>
  );

  return (
    <AppModalShell
      open={isOpen}
      title={modalTitle}
      onClose={handleClose}
      closeAriaLabel={closeAriaLabel || t(`${i18nKeyPrefix}.close_alt`)}
      maxWidthClassName="max-w-[392px]"
      desktopMaxHeight="min(88vh, 680px)"
      panelStyle={{ height: "min(88vh, 680px)" }}
      bodyClassName="flex flex-col overflow-hidden px-4 pb-4 pt-0"
      footer={carouselFooter}
      zIndexClassName="z-[13000]"
      // Mount directly under <body>. iOS Safari can fail to composite a
      // transformed/backdrop-filter modal when it is nested inside the fixed
      // #modal-root used by event details, leaving the join CTA looking dead.
      // The shell and visual contract stay shared; only the portal target is
      // made independent from the event page stacking context.
      forcePortalToBody
    >
      {content}
    </AppModalShell>
  );
}
